/** LRU for retained derived pixel surfaces. Source bytes and temporary paint buffers are separate. */
export class PixelBudget {
  private entries = new Map<
    object,
    { bytes: number; category: string; dispose: () => void }
  >();
  private bytes = 0;
  evictions = 0;
  constructor(readonly limit = 96 * 1024 * 1024) {}
  touch(key: object) {
    const entry = this.entries.get(key);
    if (entry) {
      this.entries.delete(key);
      this.entries.set(key, entry);
    }
  }
  add(key: object, bytes: number, category: string, dispose: () => void) {
    this.remove(key);
    this.entries.set(key, { bytes, category, dispose });
    this.bytes += bytes;
    // The caller consumes an oversized single surface once, then explicitly releases it.
    for (const candidate of this.entries.keys()) {
      if (this.bytes <= this.limit || candidate === key) break;
      this.evict(candidate);
    }
  }
  remove(key: object) {
    const entry = this.entries.get(key);
    if (entry) {
      this.bytes -= entry.bytes;
      this.entries.delete(key);
    }
  }
  private evict(key: object) {
    const entry = this.entries.get(key);
    if (!entry) return;
    this.remove(key);
    this.evictions++;
    entry.dispose();
  }
  clear(categories: readonly string[]) {
    for (const [key, entry] of this.entries) {
      if (!categories.includes(entry.category)) continue;
      this.remove(key);
      entry.dispose();
    }
  }
  trim() {
    for (const key of this.entries.keys()) {
      if (this.bytes <= this.limit) break;
      this.evict(key);
    }
  }
  stats() {
    const categories: Record<string, number> = {};
    for (const e of this.entries.values())
      categories[e.category] = (categories[e.category] ?? 0) + e.bytes;
    return {
      rgbaBytes: this.bytes,
      budget: this.limit,
      entries: this.entries.size,
      evictions: this.evictions,
      categories,
    };
  }
}
